import { useEffect, useState } from 'react';
import Dialogo from '@/components/Dialogo';
import CamposValores, { valoresIniciais } from '@/components/leads/CamposValores';
import { validarComparecimento } from '@/leads/regras';
import { apenasNegociadores, opcoesComGravado, temFuncao } from '@/equipe/funcoes';
import { nomeDoSistema } from '@/lib/sistemaDaClinica';
import { paraNumero } from '@/leads/recebimentos';

/**
 * ★★ WI — A BAIXA "COMPARECEU" PASSA A PEDIR O QUE O FUNIL PEDE.
 *
 * Até a WI, marcar "Compareceu" na agenda ou na ficha entrava em `compareceu` **sem
 * código, sem nome confirmado e sem valor** — e as rules deixavam passar por um passe
 * próprio (`formaDaBaixaDaAgenda`, que esta peça removeu). Resultado medido: dos 227 leads
 * em `compareceu`, **2** tinham código. A regra era duas; agora é uma.
 *
 * ★★ ESTE DIÁLOGO LÊ O LEAD, E O `DetalheAgendamento` AO LADO DELE NÃO PODE.
 *
 * A diferença não é descuido. As rules reservam `leads` a `podeVendas`, e **esta tela só
 * existe para quem tem esse papel** — o botão que a abre está atrás de `podeVendas`
 * (`DetalheAgendamento`, WI §2), e a guarda de fonte `agenda/wi.guardas.test.js` falha se
 * alguém o soltar. Se um dia a recepção voltar a dar baixa, **isto quebra**, e quebra com
 * `permission-denied` na cara de quem menos pode resolver. Quem reabrir esse botão tem de
 * reabrir a leitura junto, ou mover a baixa para uma callable (deploy de functions).
 *
 * ★ O componente dos campos é o MESMO do funil (`CamposValores`, modo `comparecimento`).
 * Recriar os campos aqui é o que a guarda de fonte da WD-c2 existe para impedir: duas
 * cópias divergiriam no primeiro ajuste, e o mesmo lead teria regras diferentes conforme
 * o caminho — que é exatamente o defeito que esta peça veio fechar.
 */
export default function DialogoBaixaComparecimento({
  aberto = false,
  lead = null,
  clinica = null,
  consulta = null,
  ocupado = false,
  equipe = [],
  usuario = null,
  aoFechar,
  aoConfirmar,
}) {
  const [valores, setValores] = useState(() => valoresIniciais(null));
  const [negociadorId, setNegociadorId] = useState('');
  const [erro, setErro] = useState(null);

  /**
   * ★ Os valores nascem do LEAD a cada abertura — e a caixa de confirmação do nome nasce
   * DESMARCADA (é `valoresIniciais` quem garante). Herdá-la transformaria o ato em padrão,
   * que é o que a medição da WH mostrou não funcionar.
   */
  useEffect(() => {
    if (!aberto) return;
    setValores(valoresIniciais(lead));
    /**
     * ★★ WO lote 3c — O PADRÃO DO NEGOCIADOR: quem já está gravado no lead; senão **eu**,
     * se eu exerço a função; senão **vazio** — e aí é obrigatório escolher.
     *
     * ⚠ O padrão "eu" cobre o caso comum (o negociador dá a própria baixa) sem nunca
     * atribuir trabalho a outra pessoa por descuido: para quem NÃO é negociador, o campo
     * nasce vazio e a tela não deixa gravar.
     */
    const euSouNegociador = temFuncao((equipe ?? []).find((u) => u.id === usuario?.uid) ?? null, 'negociador');
    setNegociadorId(lead?.negociadorId || (euSouNegociador ? usuario.uid : ''));
    setErro(null);
  }, [aberto, lead?.id, equipe, usuario?.uid]);

  if (!aberto) return null;

  const sistema = nomeDoSistema(clinica?.config);
  const negociadores = opcoesComGravado(
    apenasNegociadores(equipe), lead?.negociadorId, lead?.negociadorNome,
  );
  const dados = {
    negociadorId,
    negociadorNome: negociadores.find((u) => u.id === negociadorId)?.nome ?? '',
    codigoCareflow: valores.codigoCareflow,
    nome: valores.nome,
    nomeConfirmado: valores.nomeConfirmado,
    valorOrcado: paraNumero(valores.valorOrcado),
    semOrcamento: valores.semOrcamento === true,
  };

  async function enviar(e) {
    e.preventDefault();
    /**
     * ★ A MESMA VERIFICAÇÃO DO FUNIL, e a mesma função pura — `validarComparecimento`, que
     * é literalmente a que `validarEtapa` usa por dentro. Ela roda aqui para dar mensagem
     * clara; as rules continuam sendo a autoridade. Sem esta chamada a pessoa levaria
     * `permission-denied` em vez de "informe o código".
     *
     * ⚠⚠ WO lote 3c — A RESSALVA DE CIMA DEIXOU DE VALER, e isto é reversão de decisão.
     *
     * O comentário anterior dizia: *"NÃO é `validarEtapa('compareceu', …)`: aquela exige
     * também o NEGOCIADOR (V4), que a baixa deliberadamente não exige — a medição dos 223
     * de 227 leads sem negociador. Exigi-lo aqui travaria quase toda baixa."*
     *
     * ★ Aquela medição era sobre o PASSADO (leads que JÁ estavam sem negociador). O D3
     * cobra o FUTURO: quem dá a baixa diz quem vai negociar. ⚠ E agora as **rules impõem**
     * — sem este campo, a gravação levaria `permission-denied`, que é a pior forma de
     * descobrir a regra.
     */
    const problema = validarComparecimento({ ...lead, ...dados })
      || (negociadorId ? null : 'Escolha o negociador para registrar o comparecimento.');
    if (problema) { setErro(problema); return; }
    setErro(null);
    try {
      await aoConfirmar?.(dados);
    } catch (falha) {
      setErro(falha?.message ?? 'Não foi possível registrar o comparecimento.');
    }
  }

  return (
    <Dialogo
      titulo={`Compareceu — ${lead?.nome || consulta?.nome || 'paciente'}`}
      aberto={aberto}
      aoFechar={aoFechar}
    >
      <form className="dialogo-corpo" onSubmit={enviar}>
        <CamposValores
          valores={valores}
          aoMudar={setValores}
          lead={lead}
          clinica={clinica}
          disabled={ocupado}
          momento="comparecimento"
        />
        {/* ★★ WO lote 3c — o negociador, exigido pelas rules a partir deste lote. */}
        <label className="campo">
          <span>Negociador</span>
          <select
            value={negociadorId}
            disabled={ocupado}
            onChange={(e) => setNegociadorId(e.target.value)}
            aria-label="Negociador"
          >
            <option value="">Escolha o negociador</option>
            {negociadores.map((u) => (
              <option key={u.id} value={u.id}>{u.nome}</option>
            ))}
          </select>
          <span className="campo-ajuda">
            Quem vai negociar com este paciente. É quem recebe o crédito do fechamento.
          </span>
        </label>
        <p className="campo-ajuda">
          O código é o que faz a importação do {sistema} casar os pagamentos com este lead. A
          oportunidade é o que mostra quanto do que a clínica orça vira contrato. Se não houve
          orçamento, marque a caixa.
        </p>

        {erro && <p className="erro">{erro}</p>}

        <footer className="dialogo-acoes">
          <button type="button" className="btn-secundario" onClick={aoFechar} disabled={ocupado}>
            Cancelar
          </button>
          <button type="submit" className="btn-primario" disabled={ocupado}>
            {ocupado ? 'Salvando…' : 'Confirmar comparecimento'}
          </button>
        </footer>
      </form>
    </Dialogo>
  );
}
